import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { parseLocalDate } from '../../../utils/dateUtils';

const EventDetailsModal = ({ event, isOpen, onClose }) => {
  if (!isOpen || !event) return null;

  const getEventTypeColor = (type) => {
    const colors = {
      applications: 'bg-blue-500',
      exams: 'bg-red-500',
      results: 'bg-green-500',
      interviews: 'bg-purple-500',
      registration: 'bg-orange-500'
    };
    return colors?.[type] || 'bg-gray-500';
  };

  const getEventTypeIcon = (type) => {
    const icons = {
      applications: 'FileText',
      exams: 'BookOpen',
      results: 'Award',
      interviews: 'Users',
      registration: 'UserPlus'
    };
    return icons?.[type] || 'Calendar';
  };

  const formatDate = (dateStr) => {
    const date = parseLocalDate(dateStr);
    return date?.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysRemaining = () => {
    const eventDate = parseLocalDate(event.date);
    const now = new Date();
    const diffTime = eventDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return "Échéance passée";
    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return "Demain";
    return `Dans ${diffDays} jours`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card border border-border rounded-lg shadow-interactive max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div className="flex items-start space-x-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getEventTypeColor(event?.type)}`}>
              <Icon name={getEventTypeIcon(event?.type)} size={24} color="white" />
            </div>
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-sm font-medium text-primary">{event?.typeLabel}</span>
                <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                  {getDaysRemaining()}
                </span>
              </div>
              <h2 className="text-xl font-heading font-bold text-foreground mb-1">
                {event?.title}
              </h2>
              <p className="text-muted-foreground">{event?.institution}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Event Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <Icon name="Calendar" size={16} className="text-muted-foreground" />
              <div>
                <div className="text-sm text-muted-foreground">Date limite</div>
                <div className="font-medium text-foreground">{formatDate(event?.date)}</div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Icon name="MapPin" size={16} className="text-muted-foreground" />
              <div>
                <div className="text-sm text-muted-foreground">Lieu</div>
                <div className="font-medium text-foreground">{event?.location}</div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Icon name="GraduationCap" size={16} className="text-muted-foreground" />
              <div>
                <div className="text-sm text-muted-foreground">Programme</div>
                <div className="font-medium text-foreground">{event?.program}</div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Icon name="Users" size={16} className="text-muted-foreground" />
              <div>
                <div className="text-sm text-muted-foreground">Places disponibles</div>
                <div className="font-medium text-foreground">{event?.availableSpots || 'Non spécifié'}</div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <h3 className="font-medium text-foreground mb-2">Description</h3>
            <p className="text-muted-foreground leading-relaxed">
              {event?.description}
            </p>
          </div>

          {/* Requirements */}
          {event?.requirements && (
            <div>
              <h3 className="font-medium text-foreground mb-3">Conditions d'admission</h3>
              <div className="bg-muted/50 rounded-lg p-4">
                <ul className="space-y-2">
                  {event?.requirements?.map((requirement, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="Check" size={16} className="text-success mt-0.5" />
                      <span className="text-sm text-foreground">{requirement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Documents Required */}
          {event?.documents && (
            <div>
              <h3 className="font-medium text-foreground mb-3">Documents requis</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {event?.documents?.map((document, index) => (
                  <div key={index} className="flex items-center space-x-2 p-2 bg-muted/50 rounded">
                    <Icon name="FileText" size={14} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{document}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preparation Resources */}
          {event?.resources && (
            <div>
              <h3 className="font-medium text-foreground mb-3">Ressources de préparation</h3>
              <div className="space-y-2">
                {event?.resources?.map((resource, index) => (
                  <a
                    key={index}
                    href={resource?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg hover:bg-muted transition-smooth"
                  >
                    <Icon name="ExternalLink" size={16} className="text-primary" />
                    <div>
                      <div className="font-medium text-foreground">{resource?.title}</div>
                      <div className="text-sm text-muted-foreground">{resource?.description}</div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 p-6 border-t border-border bg-muted/50">
          <Button
            variant="default"
            iconName="ExternalLink"
            iconPosition="right"
            onClick={() => window.open(event?.applicationUrl, '_blank')}
            className="flex-1"
          >
            Accéder à la candidature
          </Button>
          <Button
            variant="outline"
            iconName="Bell"
            iconPosition="left"
            className="flex-1"
          >
            Créer une alerte
          </Button>
          <Link to={`/institution-details?id=${event?.institutionId}`}>
            <Button variant="outline" className="w-full sm:w-auto">
              Voir l'établissement
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default EventDetailsModal;